// apps/app/src/coachmark/HelpButton.tsx
// The TopBar "Help" menu. Lists every coachmark so a returning player can
// re-arm any individual hint (selection triggers controller.resetAll() +
// openHint(id)), plus an optional "Reference help" entry that opens the
// static HelpModal.
//
// The controller is passed in (not created here) so the same instance
// drives CoachmarkHost and useCoachmarkSequence — App owns the only
// useCoachmarks() call in the tree. Without a controller (multiplayer) the
// menu only offers the reference help.

import { Button } from '@aod/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@aod/ui/components/ui/dropdown-menu';
import { BookOpen, CircleHelp, Lightbulb } from 'lucide-react';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { coachmarkContent } from './content.js';
import type { CoachmarkController } from './useCoachmarks.js';

interface HelpButtonProps {
  readonly controller?: CoachmarkController;
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly onOpenReferenceHelp?: () => void;
}

export function HelpButton({
  controller,
  open,
  onOpenChange,
  onOpenReferenceHelp,
}: HelpButtonProps) {
  const t = useT();
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="menu-help"
          {...appDom({
            id: 'menu-help',
            type: 'Dropdown',
            role: 'button',
            scope: 'header',
            label: 'Help menu',
          })}
          aria-label={t('menu.help')}
          className="h-8 px-2 lg:px-2.5"
        >
          <CircleHelp className="size-4" />
          <span className="hidden lg:inline">{t('menu.help')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[270px]"
        data-testid="menu-help-content"
        {...appDom({ id: 'menu-help-content', type: 'Dropdown', scope: 'menu' })}
      >
        {controller && (
          <>
            <DropdownMenuLabel>{t('menu.help.hints')}</DropdownMenuLabel>
            {Object.values(coachmarkContent).map((entry) => (
              <DropdownMenuItem
                key={entry.id}
                data-testid={`menu-help-hint-${entry.id}`}
                {...appDom({ id: `menu-help-hint-${entry.id}`, role: 'button', scope: 'menu' })}
                onSelect={() => {
                  controller.resetAll();
                  controller.openHint(entry.id);
                }}
              >
                <Lightbulb />
                {entry.title}
              </DropdownMenuItem>
            ))}
          </>
        )}
        {onOpenReferenceHelp && (
          <>
            {controller && <DropdownMenuSeparator />}
            <DropdownMenuItem
              data-testid="menu-help-reference"
              {...appDom({ id: 'menu-help-reference', role: 'button', scope: 'menu' })}
              onSelect={onOpenReferenceHelp}
            >
              <BookOpen />
              {t('menu.help.reference')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
